import React from "react";
import { useT } from "../lib/LanguageContext";

/**
 * The From / To date inputs every super-admin list filters by, with their one rule: a range
 * is only sent when the server will accept it. The server (`modules/listing.date_bounds`)
 * takes exactly `YYYY-MM-DD`, a real calendar day, year 0001-9999, `from` not after `to`, and
 * `to` at most 9999-12-30 (its upper bound is the start of the day AFTER `to`). Anything else
 * answers 422, which a page could only show as a failure whose Retry can never succeed.
 *
 * `from` / `to` are strings from `<input type="date">`; an empty side is simply unbounded.
 * `dateRangeProblem` names what is wrong (`"outOfRange"` covers a malformed or impossible
 * date, a 5-digit year included; `"inverted"` an end before the start), `isRangeInvalid` is
 * the yes/no the lists gate their request on. Render the component inside a `flex flex-wrap`
 * row: the message takes a full line of its own.
 */
export const MIN_DAY = "0001-01-01";
export const MAX_FROM_DAY = "9999-12-31";
export const MAX_TO_DAY = "9999-12-30";

const DAY_FORMAT = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` -> UTC milliseconds, or `null` when it is not a real calendar day in 0001-9999. */
const parseDay = (text) => {
  const m = DAY_FORMAT.exec(text);
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year < 1) return null;
  // `Date.UTC` maps years 0-99 onto 1900-1999, so the year is set separately.
  const d = new Date(0);
  d.setUTCFullYear(year, month - 1, day);
  const real = d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
  return real ? d.getTime() : null;
};

export const dateRangeProblem = (from, to) => {
  const start = from ? parseDay(from) : null;
  const end = to ? parseDay(to) : null;
  if ((from && start === null) || (to && end === null)) return "outOfRange";
  if (to && to > MAX_TO_DAY) return "outOfRange";
  if (start !== null && end !== null && start > end) return "inverted";
  return null;
};

export const isRangeInvalid = (from, to) => dateRangeProblem(from, to) !== null;

const FIELD_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

export default function DateRangeFilter({ from, to, onFromChange, onToChange, testIds = {} }) {
  const t = useT("superadmin");
  const problem = dateRangeProblem(from, to);
  // A side narrows the other only while it is itself a valid day.
  const validFrom = from && parseDay(from) !== null ? from : null;
  const validTo = to && parseDay(to) !== null ? to : null;
  return (
    <>
      <label className="flex items-center gap-1.5 text-xs text-slate-500">
        {t("sal.dateFrom")}
        <input
          type="date" value={from} min={MIN_DAY} max={validTo || MAX_FROM_DAY}
          onChange={(e) => onFromChange(e.target.value)}
          className={FIELD_CLASS} data-testid={testIds.from}
        />
      </label>
      <label className="flex items-center gap-1.5 text-xs text-slate-500">
        {t("sal.dateTo")}
        <input
          type="date" value={to} min={validFrom || MIN_DAY} max={MAX_TO_DAY}
          onChange={(e) => onToChange(e.target.value)}
          className={FIELD_CLASS} data-testid={testIds.to}
        />
      </label>
      {problem && (
        <p role="alert" className="basis-full text-sm text-rose-600" data-testid={testIds.error}>
          {t(problem === "inverted" ? "sal.rangeInvalid" : "sal.dateOutOfRange")}
        </p>
      )}
    </>
  );
}
